/* eslint-disable sort-keys */
import React from 'react';
import {useTranslation} from 'react-i18next';

import Icon from 'components/v3/Icon';
import {ROUTES, URLS} from 'constants/routes';

/**
 * 新版导航数据。结构与文案照抄设计稿 docs/designs/xiangji_home_v3/index.html 的导航栏区块。
 *
 * 链接目标分三种（渲染逻辑见 NavDropdown / MobileMenu）：
 * - to：站内路由（react-router Link，basename=/home 下不整页刷新）
 * - href：站外链接（一律走 URLS 常量，保证 utm_source/register_invite 透传）
 * - onClick：交互项（合作申请 → CooperationModal，回调由 TopNav 注入）
 */

export interface NavItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  iconBgClass: string;
  iconColorClass: string;
  to?: string;
  href?: string;
  onClick?: () => void;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/** 「合作申请」的打开回调。数据工厂每次渲染重建，回调经此注入（TopNav mount 时注册）。 */
let openCooperation: () => void = () => undefined;

export const setCooperationOpener = (fn: () => void) => {
  openCooperation = fn;
};

/**
 * 导航数据工厂（i18n）：文案走 common 词典，icon/样式类/链接保持静态结构。
 * 必须在组件函数体内调用（useTranslation 约束），使用方见 TopNav 与 MobileMenu。
 */
export const useNavGroups = (): NavGroup[] => {
  const {t} = useTranslation('common');

  return [
    {
      label: t('nav.groups.products'),
      items: [
        {
          title: t('nav.items.imageTranslator.title'),
          description: t('nav.items.imageTranslator.description'),
          icon: (
            <Icon
              paths={[
                'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
              ]}
            />
          ),
          iconBgClass: 'bg-indigo-50',
          iconColorClass: 'text-indigo-600',
          to: ROUTES.imageTranslator,
        },
        {
          title: t('nav.items.videoTranslator.title'),
          description: t('nav.items.videoTranslator.description'),
          icon: (
            <Icon
              paths={[
                'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
              ]}
            />
          ),
          iconBgClass: 'bg-violet-50',
          iconColorClass: 'text-violet-600',
          to: ROUTES.videoTranslator,
        },
        {
          title: t('nav.items.detailPage.title'),
          description: t('nav.items.detailPage.description'),
          icon: (
            <Icon
              paths={[
                'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
              ]}
            />
          ),
          iconBgClass: 'bg-emerald-50',
          iconColorClass: 'text-emerald-600',
          // 主图/详情图生成：原外链独立站收编为站内落地页（/listing-images，2026-09）
          to: ROUTES.listingImages,
        },
        {
          title: t('nav.items.fusion.title'),
          description: t('nav.items.fusion.description'),
          icon: (
            <Icon
              paths={[
                'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
              ]}
            />
          ),
          iconBgClass: 'bg-amber-50',
          iconColorClass: 'text-amber-600',
          to: ROUTES.fusion,
        },
        {
          title: t('nav.items.marketing.title'),
          description: t('nav.items.marketing.description'),
          icon: (
            <Icon
              paths={['M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z', 'M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z']}
            />
          ),
          iconBgClass: 'bg-rose-50',
          iconColorClass: 'text-rose-600',
          to: ROUTES.marketing,
        },
      ],
    },
    {
      label: t('nav.groups.business'),
      items: [
        {
          title: t('nav.items.cooperation.title'),
          description: t('nav.items.cooperation.description'),
          icon: (
            <Icon
              paths={[
                'M18 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0ZM3 19.235v-.11a6.375 6.375 0 0 1 12.75 0v.109A12.318 12.318 0 0 1 9.374 21c-2.331 0-4.512-.645-6.374-1.766Z',
              ]}
            />
          ),
          iconBgClass: 'bg-indigo-50',
          iconColorClass: 'text-indigo-600',
          onClick: () => openCooperation(),
        },
        {
          title: t('nav.items.proxy.title'),
          description: t('nav.items.proxy.description'),
          icon: (
            <Icon
              paths={[
                'M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
              ]}
            />
          ),
          iconBgClass: 'bg-emerald-50',
          iconColorClass: 'text-emerald-600',
          // 代理返现跳转暂时下线（业务 2026-09-16）：保留入口展示，渲染为不可跳转项；页面本身 /proxy-mechanism 仍可直达
        },
      ],
    },
    {
      label: t('nav.groups.help'),
      items: [
        {
          title: t('nav.items.guide.title'),
          description: t('nav.items.guide.description'),
          icon: (
            <Icon
              paths={[
                'M4.26 10.147a60.438 60.438 0 0 0-.491 6.347A48.62 48.62 0 0 1 12 20.904a48.62 48.62 0 0 1 8.232-4.41 60.46 60.46 0 0 0-.491-6.347m-15.482 0a50.636 50.636 0 0 0-2.658-.813A59.906 59.906 0 0 1 12 3.493a59.903 59.903 0 0 1 10.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.717 50.717 0 0 1 12 13.489a50.702 50.702 0 0 1 7.74-3.342M6.75 15a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Zm0 0v-3.675A55.378 55.378 0 0 1 12 8.443m-7.007 11.55A5.981 5.981 0 0 0 6.75 15.75v-1.5',
              ]}
            />
          ),
          iconBgClass: 'bg-blue-50',
          iconColorClass: 'text-blue-600',
          to: ROUTES.guide,
        },
        {
          title: t('nav.items.languages.title'),
          description: t('nav.items.languages.description'),
          icon: (
            <Icon
              paths={[
                'M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418',
              ]}
            />
          ),
          iconBgClass: 'bg-teal-50',
          iconColorClass: 'text-teal-600',
          to: ROUTES.language,
        },
      ],
    },
    {
      label: t('nav.groups.about'),
      items: [
        {
          title: t('nav.items.contact.title'),
          description: t('nav.items.contact.description'),
          icon: (
            <Icon
              paths={[
                'M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75',
              ]}
            />
          ),
          iconBgClass: 'bg-rose-50',
          iconColorClass: 'text-rose-600',
          to: ROUTES.contact,
        },
        {
          title: t('nav.items.blog.title'),
          description: t('nav.items.blog.description'),
          icon: (
            <Icon
              paths={[
                'M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25',
              ]}
            />
          ),
          iconBgClass: 'bg-amber-50',
          iconColorClass: 'text-amber-600',
          href: URLS.blogUrl,
        },
      ],
    },
  ];
};

/** 顶层导航链接（非下拉，直接点击跳转）：浏览器插件、API对接，2026-09-17 从「产品服务」下拉中提出置顶 */
export interface TopNavLink {
  title: string;
  to?: string;
  href?: string;
}

export const useTopNavLinks = (): TopNavLink[] => {
  const {t} = useTranslation('common');

  return [
    {title: t('nav.items.extension.title'), to: ROUTES.extension},
    {title: t('nav.items.api.title'), href: URLS.developerAPIDoc},
  ];
};
